import * as React from 'react';
import { Search, X } from 'lucide-react';
import { cn } from './lib/utils';

/**
 * Multi-filter search input with + separator for AND logic.
 *
 * Supports compound queries: "Access+Daily" matches items containing
 * BOTH "Access" AND "Daily" across any searchable field.
 *
 * <FilterSearch value={q} onChange={setQ} placeholder="Search... (use + for multi-filter)" />
 *
 * Use the static helper to filter data:
 *   const filtered = FilterSearch.filter(items, query, (item) => [item.name, item.domain]);
 *
 * size="md"   → h-8, text-body, rounded-lg, ring-2  (default — filter bars)
 * size="sm"   → h-7, text-body-sm, rounded, ring-1, bg-surface  (compact — panel-embedded)
 *
 * width="sm"  → w-40
 * width="md"  → w-52
 * width="lg"  → w-72
 * width="full"→ w-full (default)
 */

const WIDTH = {
  sm:   'w-40',
  md:   'w-52',
  lg:   'w-72',
  full: 'w-full',
};

const SIZE = {
  md: {
    input: 'h-8 rounded-lg border-nn-border bg-nn-white pl-8 pr-7 text-body focus:ring-2 focus:ring-aqua/40',
    icon:  'left-2.5 h-3.5 w-3.5',
    clear: 'h-3.5 w-3.5',
  },
  sm: {
    input: 'h-7 rounded border-nn-border bg-surface pl-7 pr-7 text-body-sm focus:ring-1 focus:ring-aqua/40',
    icon:  'left-2 h-3.5 w-3.5',
    clear: 'h-3 w-3',
  },
};

function FilterSearch({ value, onChange, placeholder = 'Search\u2026 (use + for multi-filter)', width = 'full', size = 'md', className, ...props }) {
  const s = SIZE[size] ?? SIZE.md;

  return (
    <div className={cn('relative flex items-center', WIDTH[width] ?? WIDTH.full, className)}>
      <Search className={cn('absolute text-text-muted pointer-events-none shrink-0', s.icon)} />
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className={cn(
          'w-full border text-text-dark',
          'placeholder:text-text-muted',
          'focus:outline-none focus:border-aqua',
          'transition-colors',
          s.input
        )}
        {...props}
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange('')}
          className="absolute right-2 text-text-muted hover:text-text-dark transition-colors"
          aria-label="Clear search"
        >
          <X className={s.clear} />
        </button>
      )}
    </div>
  );
}

/**
 * Static filter helper — applies + multi-filter AND logic.
 *
 * @param {Array} items — data array to filter
 * @param {string} query — raw search string (may contain + separators)
 * @param {Function} getSearchableFields — (item) => string[] of searchable values
 * @returns {Array} filtered items where ALL terms match across any field
 *
 * Usage:
 *   FilterSearch.filter(tasks, search, (t) => [t.taskName, t.domain, t.cadence]);
 */
FilterSearch.filter = function filterItems(items, query, getSearchableFields) {
  if (!query || !query.trim()) return items;
  const terms = query.split('+').map(t => t.trim().toLowerCase()).filter(Boolean);
  if (terms.length === 0) return items;

  return items.filter((item) => {
    const fields = getSearchableFields(item).map(f => (f || '').toLowerCase());
    const joined = fields.join(' ');
    return terms.every(term => joined.includes(term));
  });
};

export { FilterSearch };
